/**
 * AGC: Tournaments Page Component
 * AGC: Displays all tournaments (ongoing, open, closed) with history
 * AGC: Shows TournamentsList for overview, TournamentLobby for detail view
 */

import React, { useState } from 'react'
import { Trophy } from 'lucide-react'
import { TournamentsList } from './TournamentsList'
import type { ActivePage } from '../../MainMenu'

import type { BottomNavKey } from '../../BottomNav'

interface TournamentsProps {
  onNavigateToPage?: (page: ActivePage) => void
  onNavClick?: (nav: BottomNavKey) => void
  activeNav?: BottomNavKey
  onOpenSuggestion?: () => void
  onOpenSettings?: () => void
  onOpenInventory?: () => void
  initialTournamentId?: string | null // AGC: Initial tournament ID to show (from match completion)
  onTournamentIdChange?: (tournamentId: string | null) => void // AGC: Callback when tournament ID changes
}

export const Tournaments: React.FC<TournamentsProps> = ({
  onNavigateToPage,
  initialTournamentId,
  onTournamentIdChange,
}) => {
  const [selectedTournamentId, setSelectedTournamentId] = useState<string | null>(initialTournamentId || null)
  
  // AGC: Sync with external tournament ID changes (e.g., from match completion or "Join Contest" popup)
  React.useEffect(() => {
    if (initialTournamentId !== undefined) {
      // AGC: If initialTournamentId is explicitly null, clear selection (navigation from Games page)
      // AGC: If initialTournamentId has a value, set it (navigation from popup or match completion)
      setSelectedTournamentId(initialTournamentId)
    }
  }, [initialTournamentId])
  
  // AGC: Notify parent of tournament ID changes
  const handleTournamentIdChange = React.useCallback((tournamentId: string | null) => {
    setSelectedTournamentId(tournamentId)
    onTournamentIdChange?.(tournamentId)
  }, [onTournamentIdChange])
  const sharedTopPaddingStyle = { paddingTop: 'calc(env(safe-area-inset-top, 0px) + 72px)' }

  return (
    <main
      className="pb-[88px] px-3 text-white flex flex-col gap-1.5"
      style={sharedTopPaddingStyle}
    >
      {/* AGC: Hero Header (No Card) - Following Profile.tsx pattern */}
      <section className="pb-2 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[9px] uppercase tracking-[0.25em] text-slate-500">TOURNAMENTS</p>
            <h1 className="text-2xl font-bold leading-tight text-white">Contests</h1>
            <p className="text-[11px] text-slate-400">
              {selectedTournamentId ? 'Tournament Details' : 'Join active tournaments or view history'}
            </p>
          </div>
          <div className="text-[10px] px-2.5 py-1 rounded-lg bg-white/5 border border-white/10">
            <Trophy className="w-4 h-4 text-yellow-400" />
          </div>
        </div>
      </section>

      {/* AGC: Tournaments List or Detail View */}
      <div className="mt-2">
        <TournamentsList
          onNavigateToPage={onNavigateToPage}
          onSelectTournament={handleTournamentIdChange}
          selectedTournamentId={selectedTournamentId}
        />
      </div>
    </main>
  )
}

